"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api";
import { queryKeys, ENV_PREFIX } from "../../lib/query-keys";
import { scheduleIdle } from "../../lib/idle";
import { buildEventPath } from "../../lib/routes";
import { useNetworkQuality } from "../../hooks/useNetworkQuality";


type BannerEvent = {
    id: string;
    shortId?: string | null;
    title: string;
    imageUrl: string | null;
    hostName?: string;
    date?: string;
};

type FeaturedEntry = {
    id: string;
    event: {
        id: string;
        shortId?: string | null;
        title: string;
        imageUrl: string | null;
        hostName: string;
        date: string;
    };
};

interface HeroBannerProps {
    initialData?: unknown[];
}

export function HeroBanner({ initialData }: HeroBannerProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const carouselRef = useRef<HTMLDivElement | null>(null);
    const queryClient = useQueryClient();
    const networkQuality = useNetworkQuality();

    // Detect prefers-reduced-motion via external store subscription
    const prefersReducedMotion = useSyncExternalStore(
        (callback) => {
            const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
            mq.addEventListener("change", callback);
            return () => mq.removeEventListener("change", callback);
        },
        () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        () => false,
    );

    // Pause carousel when any element inside it receives keyboard focus
    const onFocusIn = () => setHasFocus(true);
    const onFocusOut = (e: React.FocusEvent) => {
        if (!carouselRef.current?.contains(e.relatedTarget as Node)) {
            setHasFocus(false);
        }
    };

    const { data, isLoading } = useQuery({
        queryKey: queryKeys.events.featuredBanner(),
        queryFn: () => apiFetch<FeaturedEntry[]>("featured-events"),
        initialData: initialData as FeaturedEntry[] | undefined,
        staleTime: 5 * 60_000,
    });

    // Featured events are curated server-side — no client-side date filtering
    // (Date.now() causes hydration mismatch with ISR)
    const upcomingEntries = (() => {
        if (!data || data.length === 0) {
            return [];
        }
        return data.filter((entry) => {
            const value = new Date(entry.event.date);
            return !Number.isNaN(value.getTime());
        });
    })();

    const banners: BannerEvent[] = (() => {
        if (!upcomingEntries || upcomingEntries.length === 0) {
            return [];
        }
        return upcomingEntries.map((entry) => ({
            id: entry.event.id,
            shortId: entry.event.shortId,
            title: entry.event.title,
            imageUrl: entry.event.imageUrl ?? null,
            hostName: entry.event.hostName,
            date: entry.event.date,
        }));
    })();

    useEffect(() => {
        if (!upcomingEntries || upcomingEntries.length === 0) {
            return;
        }
        return scheduleIdle(() => {
            upcomingEntries.slice(0, 6).forEach((entry) => {
                const eventId = entry.event.id;
                if (!eventId) {
                    return;
                }
                queryClient.prefetchQuery({
                    queryKey: [ENV_PREFIX, "event", eventId],
                    queryFn: () => apiFetch<unknown>(`events/${eventId}`),
                    staleTime: 30_000,
                });
            });
        });
    }, [upcomingEntries, queryClient]);

    // Auto-rotate banners (pauses when user pauses, focuses, or prefers reduced motion)
    // Including activeIndex resets the 5s timer whenever the user manually changes slide
    useEffect(() => {
        if (banners.length <= 1) {
            return;
        }
        if (isPaused || hasFocus || prefersReducedMotion) {
            return;
        }
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % banners.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [banners.length, isPaused, hasFocus, prefersReducedMotion, activeIndex]);

    // Clamp activeIndex to valid range when banners change
    const clampedIndex = banners.length > 0 ? activeIndex % banners.length : 0;
    const activeBanner = banners[clampedIndex];
    if (isLoading) {
        return (
            <section className="px-4 pt-4 md:px-6 md:pt-8" aria-busy="true" aria-label="Loading featured events">
                <div className="mx-auto max-w-6xl">
                    <div className="aspect-[16/9] w-full animate-pulse rounded-2xl bg-[var(--hopp-panel)] sm:aspect-[16/7]" />
                    {/* Reserve space for carousel dots to prevent CLS */}
                    <div className="mt-3 flex justify-center gap-1.5">
                        <div className="h-1.5 w-6 rounded-full bg-white/10" />
                        <div className="h-1.5 w-1.5 rounded-full bg-white/10" />
                        <div className="h-1.5 w-1.5 rounded-full bg-white/10" />
                    </div>
                </div>
            </section>
        );
    }

    if (banners.length === 0) {
        return null;
    }

    return (
        <section className="px-4 pt-4 md:px-6 md:pt-8" aria-live="polite">
            <div className="mx-auto max-w-6xl">
                {/* Banner Container */}
                <div
                    ref={carouselRef}
                    className="relative"
                    role="region"
                    aria-roledescription="carousel"
                    aria-label="Featured events"
                    onFocus={onFocusIn}
                    onBlur={onFocusOut}
                >
                    <Link href={activeBanner ? buildEventPath(activeBanner.shortId ?? activeBanner.id, activeBanner.title) : "#"}>
                        <div
                            key={clampedIndex}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`Slide ${clampedIndex + 1} of ${banners.length}: ${activeBanner?.title ?? ""}`}
                            className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl sm:aspect-[16/7]"
                        >
                            {/* Background Image — quality adapts to network speed */}
                            {activeBanner?.imageUrl ? (
                                <Image
                                    src={activeBanner.imageUrl}
                                    alt={activeBanner.title}
                                    fill
                                    priority
                                    quality={networkQuality}
                                    sizes="100vw"
                                    className="object-cover"
                                />
                            ) : (
                                <div className="h-full w-full bg-gradient-to-br from-[rgba(58,92,255,0.25)] via-[rgba(16,16,24,0.9)] to-[rgba(0,0,0,0.9)]" />
                            )}

                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                            {/* Content */}
                            <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                                {activeBanner?.hostName && (
                                    <span className="mb-2 inline-flex rounded-full bg-black/70 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#EDEDED]">
                                        {activeBanner.hostName}
                                    </span>
                                )}
                                <h1 className="font-heading text-lg font-bold text-white md:text-2xl">
                                    {activeBanner?.title}
                                </h1>
                            </div>
                        </div>
                    </Link>
                    {banners.length > 1 ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-3 md:px-6">
                            <button
                                type="button"
                                onClick={() =>
                                    setActiveIndex(
                                        (prev) =>
                                            (prev - 1 + banners.length) % banners.length,
                                    )
                                }
                                className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/70 transition hover:text-white"
                                aria-label="Previous featured event"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M15 6l-6 6 6 6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    setActiveIndex((prev) => (prev + 1) % banners.length)
                                }
                                className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/70 transition hover:text-white"
                                aria-label="Next featured event"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </button>
                        </div>
                    ) : null}
                    {banners.length > 1 ? (
                        <button
                            type="button"
                            onClick={() => setIsPaused((prev) => !prev)}
                            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/70 transition hover:text-white"
                            aria-label={isPaused ? "Resume carousel" : "Pause carousel"}
                        >
                            {isPaused ? (
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M5 4l14 8-14 8V4z" />
                                </svg>
                            ) : (
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M8 5v14M16 5v14" />
                                </svg>
                            )}
                        </button>
                    ) : null}
                </div>

                {/* Carousel Dots */}
                {banners.length > 1 && (
                    <div className="mt-3 flex justify-center gap-1.5">
                        {banners.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`h-1.5 rounded-full transition-all ${idx === clampedIndex
                                        ? "w-6 bg-[#EDEDED]"
                                        : "w-1.5 bg-white/20"
                                    }`}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
